import Checkbox from '@material-ui/core/Checkbox'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import FormHelperText from '@material-ui/core/FormHelperText'
import Grid from '@material-ui/core/Grid'
import InputLabel from '@material-ui/core/InputLabel'
import MenuItem from '@material-ui/core/MenuItem'
import OutlinedInput from '@material-ui/core/OutlinedInput'
import React, { useState, useEffect, useRef } from 'react'
import Select from '@material-ui/core/Select'
import TextField from '@material-ui/core/TextField'
import Autocomplete from '@material-ui/lab/Autocomplete'

import { errors as errorUtils, strings } from '../../../../../../utils'

import { FormControl } from '../../../../../../components/Forms'
import { Subtitle } from '../../../../../../components'
import { ZipCodeInput } from '../../../../../../components/Forms/Inputs'
import { addressService, appService } from '../../../../../../services'

import Results from './Results'
import WeakStreetResults from './WeakStreetResults'

const { keepOnlyNumbers, limit, upcase } = strings

const handleText = text => upcase(limit(text, 250))

const defaultAddress = {
  cityId: undefined,
  cityName: '',
  cityRef: undefined,
  cityZipcode: undefined,
  complement: '',
  neighborhood: '',
  number: '',
  results: [],
  searched: false,
  selectedWeakNeighborhoodId: undefined,
  source: undefined,
  stateAcronym: undefined,
  stateId: undefined,
  street: '',
  streetId: undefined,
  weak: false,
  weakNeighborhoods: [],
  weakStreetId: undefined,
  withoutNumber: false,
  zipcode: '',
}

const Search = ({
  address,
  chooseAddressFromSearch,
  errors,
  loading,
  validating,
  setLoading,
  update,
}) => {
  const hasError = errorUtils.hasError(errors)
  const errorMessage = errorUtils.errorMessage(errors)

  const [results, setResults] = useState(address.results || [])
  const [notFound, setNotFound] = useState(false)
  const [outsideZone, setOutsideZone] = useState(false)
  const [searched, setSearched] = useState(address.searched || false)
  const [states, setStates] = useState([])
  const [cities, setCities] = useState([])
  const [autocomplete, setAutocomplete] = useState('')
  const [weakNeighborhoods, setWeakNeighborhoods] = useState(
    address.weakNeighborhoods || []
  )
  const lastFetchedZipcode = useRef(null)

  const isResolved = () =>
    !!address.streetId ||
    !!address.weakStreetId ||
    !!address.cityZipcode ||
    address.weak

  const isWeakStreetSearch = () => address.source === 'weakStreet'

  const canDisplayWeak = () =>
    !appService.getLocalPublicCall().mustResideOnZone && searched && notFound

  const shouldShowReadonlyLocation = () =>
    !address.weak &&
    (isResolved() || (!!address.stateAcronym && !!address.cityName))

  const findCityByName = name => cities.find(i => i.label === name)
  const findStateAcronym = id => states.find(i => i.value === id).label

  const tryToGetAvailableStates = async () => {
    setLoading(true)

    const {
      success,
      payload: { states: _states },
    } = await addressService.getAllStates()

    if (success) {
      setStates(_states)
    }

    setLoading(false)
  }

  const tryToGetAvailableCities = async stateId => {
    setLoading(true)

    const {
      success,
      payload: { cities: _cities },
    } = await addressService.getCitiesByState(stateId)

    if (success) {
      setCities(_cities)
    }

    setLoading(false)
  }

  const onChangeState = stateId => {
    update({
      cityId: undefined,
      cityName: '',
      cityRef: undefined,
      cityZipcode: undefined,
      stateId,
      stateAcronym: findStateAcronym(stateId),
      streetId: undefined,
      weakStreetId: undefined,
      street: '',
      neighborhood: '',
    })

    tryToGetAvailableCities(stateId)
  }

  const onChangeCity = cityName => {
    const { stateId, stateAcronym, zipcode, weak } = address
    const { ref, value } = findCityByName(cityName)

    update({
      cityId: value,
      cityName,
      cityRef: ref,
      stateId,
      stateAcronym,
      zipcode,
      weak,
      streetId: undefined,
      weakStreetId: undefined,
      street: '',
      neighborhood: '',
    })
  }

  const applyUnifiedCity = city => {
    update({
      ...defaultAddress,
      cityId: city.cityId,
      cityName: city.cityName,
      cityRef: city.cityRef,
      cityZipcode: city.cityZipcode || city.zipcode,
      stateId: city.stateId,
      stateAcronym: city.stateAcronym,
      zipcode: city.zipcode,
      searched: true,
    })
  }

  const applyStreet = (street, allResults) => {
    chooseAddressFromSearch({
      id: street.id,
      name: street.name || street.label,
      neighborhood: street.neighborhood,
      zipcode: street.zipcode,
      results: allResults,
      cityId: street.cityId,
      cityName: street.cityName,
      stateId: street.stateId,
      stateAcronym: street.stateAcronym,
      cityRef: street.cityRef,
      type: street.type,
      neighborhoodId: street.neighborhoodId,
    })
  }

  const clearWeakStreet = attrs =>
    update({
      weakStreetId: undefined,
      street: '',
      neighborhood: '',
      ...attrs,
    })

  const resetAddress = zipcode => {
    update({
      ...defaultAddress,
      zipcode,
    })
    setResults([])
    setNotFound(false)
    setOutsideZone(false)
    setSearched(false)
    setWeakNeighborhoods([])
    lastFetchedZipcode.current = null
  }

  const tryToFindByZipcode = async zipcode => {
    const digits = keepOnlyNumbers(zipcode)

    if (digits.length !== 8) return
    if (lastFetchedZipcode.current === digits) return

    lastFetchedZipcode.current = digits
    setLoading(true)

    const {
      success,
      payload: {
        addresses,
        unifiedCity,
        outsideZone: _outsideZone,
        source,
        neighborhoods,
      },
    } = await addressService.searchAddressByZipcode(digits)

    setOutsideZone(success && _outsideZone)
    setWeakNeighborhoods(success ? neighborhoods : [])

    if (success) {
      if (unifiedCity) {
        applyUnifiedCity(unifiedCity)
        setResults([])
        setNotFound(false)
        setSearched(true)
      } else if (addresses.length === 1) {
        update({
          source,
          weakNeighborhoods: neighborhoods,
          selectedWeakNeighborhoodId: undefined,
        })
        applyStreet(addresses[0], addresses)
        setResults(addresses)
        setNotFound(false)
        setSearched(true)
      } else if (addresses.length > 1) {
        setResults(addresses)
        setNotFound(false)
        setSearched(true)
        update({
          ...defaultAddress,
          zipcode,
          searched: true,
          source,
          results: addresses,
          weakNeighborhoods: neighborhoods,
          stateId: addresses[0].stateId,
          stateAcronym: addresses[0].stateAcronym,
          cityId: addresses[0].cityId,
          cityName: addresses[0].cityName,
          cityRef: addresses[0].cityRef,
        })
      } else {
        setResults([])
        setNotFound(true)
        setSearched(true)
        update({ searched: true })
      }
    } else {
      setResults([])
      setNotFound(true)
      setSearched(true)
      update({ searched: true })
    }

    setLoading(false)
  }

  const onChangeZipcode = value => {
    const digits = keepOnlyNumbers(value)

    if (digits.length < 8 && isResolved()) {
      resetAddress(value)
      return
    }

    update({ zipcode: value })

    if (digits.length === 8) {
      tryToFindByZipcode(value)
    }
  }

  // Edição de inscrição com Endereço Extra: busca de novo os cadastros do CEP
  // para reabrir os seletores de bairro/logradouro com a escolha salva. Se o
  // endereço não estiver mais disponível, mantém os dados salvos como estão.
  const tryToRestoreWeakStreet = async () => {
    const digits = keepOnlyNumbers(address.zipcode || '')

    if (digits.length !== 8) return

    lastFetchedZipcode.current = digits
    setLoading(true)

    const {
      success,
      payload: { addresses, source, neighborhoods },
    } = await addressService.searchAddressByZipcode(digits)

    const selected =
      success && source === 'weakStreet'
        ? addresses.find(i => `${i.id}` === `${address.weakStreetId}`)
        : undefined

    if (selected) {
      setResults(addresses)
      setWeakNeighborhoods(neighborhoods)
      setNotFound(false)
      setSearched(true)
      update({
        searched: true,
        source,
        results: addresses,
        weakNeighborhoods: neighborhoods,
        weakStreetId: selected.id,
        selectedWeakNeighborhoodId: selected.neighborhoodId,
      })
    }

    setLoading(false)
  }

  useEffect(() => {
    const digits = keepOnlyNumbers(address.zipcode || '')

    if (address.weakStreetId && !(address.results || []).length) {
      tryToRestoreWeakStreet()
    } else if (digits.length === 8 && !isResolved() && !searched) {
      tryToFindByZipcode(address.zipcode)
    }
  }, [])

  useEffect(() => {
    if (address.weak) {
      tryToGetAvailableStates()
    }
  }, [address.weak])

  useEffect(() => {
    if (address.weak && address.stateId) {
      tryToGetAvailableCities(address.stateId)
    }
  }, [address.weak, address.stateId])

  return (
    <>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Subtitle>Endereço do candidato</Subtitle>
        </Grid>

        <Grid item md={3} sm={4} xs={12}>
          <FormControl variant="outlined" error={hasError('zipcode')}>
            <InputLabel htmlFor="zipCode">CEP</InputLabel>
            <OutlinedInput
              autoFocus
              disabled={loading || validating}
              error={hasError('zipcode')}
              label="CEP"
              variant="outlined"
              id="zipCode"
              name="zipCode"
              value={address.zipcode || ''}
              onChange={e => onChangeZipcode(e.target.value)}
              inputComponent={ZipCodeInput}
            />

            {hasError('zipcode') && (
              <FormHelperText>{errorMessage('zipcode')}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        {shouldShowReadonlyLocation() && (
          <>
            <Grid item md={2} sm={4} xs={12}>
              <FormControl>
                <TextField
                  disabled
                  label="Estado"
                  variant="outlined"
                  value={address.stateAcronym || ''}
                  InputLabelProps={{ shrink: true }}
                />
              </FormControl>
            </Grid>

            <Grid item md={7} sm={8} xs={12}>
              <FormControl variant="outlined" error={hasError('cityId')}>
                <TextField
                  disabled
                  error={hasError('cityId')}
                  helperText={errorMessage('cityId')}
                  label="Cidade de residência"
                  variant="outlined"
                  value={address.cityName || ''}
                  InputLabelProps={{ shrink: true }}
                />
              </FormControl>
            </Grid>
          </>
        )}

        {address.weak && (
          <>
            <Grid item md={2} sm={4} xs={12}>
              <FormControl variant="outlined">
                <InputLabel htmlFor="stateId">Estado</InputLabel>
                <Select
                  disabled={loading || validating}
                  id="stateId"
                  label="Estado"
                  onChange={e => onChangeState(e.target.value)}
                  value={states.length ? address.stateId || '' : ''}
                >
                  {states.map(i => (
                    <MenuItem key={i.value} value={i.value}>
                      {i.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item md={7} sm={8} xs={12}>
              <FormControl variant="outlined" error={hasError('cityId')}>
                <Autocomplete
                  openText="Abrir"
                  closeText="Fechar"
                  clearText="Limpar"
                  noOptionsText="Nada encontrado"
                  disableClearable
                  disabled={
                    loading || !address.stateId || !cities.length || validating
                  }
                  id="cityId"
                  options={cities.map(i => i.label)}
                  value={address.cityName || ''}
                  inputValue={autocomplete}
                  onChange={(_, city) => onChangeCity(city)}
                  onInputChange={(_, val) => setAutocomplete(handleText(val))}
                  renderInput={params => (
                    <TextField
                      {...params}
                      error={hasError('cityId')}
                      InputLabelProps={
                        address.cityName ? { shrink: true } : {}
                      }
                      label="Cidade de residência"
                      variant="outlined"
                    />
                  )}
                />

                {hasError('cityId') && (
                  <FormHelperText>{errorMessage('cityId')}</FormHelperText>
                )}
              </FormControl>
            </Grid>
          </>
        )}

        {searched &&
          results.length > 1 &&
          !address.streetId &&
          !isWeakStreetSearch() && (
            <Grid item xs={12}>
              <Results
                addresses={results}
                choose={item => applyStreet(item, results)}
                disabled={address.weak || validating}
                loading={loading}
                notFound={false}
                search={address.zipcode}
                searched={searched}
                streetId={address.streetId}
              />
            </Grid>
          )}

        {searched && results.length > 1 && isWeakStreetSearch() && (
          <WeakStreetResults
            addresses={results}
            choose={item => applyStreet(item, results)}
            clearStreet={() => clearWeakStreet()}
            disabled={validating || loading}
            neighborhoods={weakNeighborhoods}
            selectedNeighborhoodId={address.selectedWeakNeighborhoodId}
            selectedStreetId={address.weakStreetId}
            selectNeighborhood={selectedWeakNeighborhoodId =>
              clearWeakStreet({ selectedWeakNeighborhoodId })
            }
          />
        )}

        {hasError('weakStreetId') && (
          <Grid item xs={12}>
            <FormHelperText error>
              Este endereço não está mais disponível para inscrição. Informe o
              CEP novamente e escolha outro endereço.
            </FormHelperText>
          </Grid>
        )}

        {canDisplayWeak() && (
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={address.weak || false}
                  disabled={validating}
                  onChange={e => {
                    if (e.target.checked) {
                      update({
                        weak: true,
                        streetId: undefined,
                        weakStreetId: undefined,
                        street: '',
                        neighborhood: '',
                        stateId: undefined,
                        stateAcronym: undefined,
                        cityId: undefined,
                        cityName: '',
                        cityRef: undefined,
                      })
                    } else {
                      update({
                        weak: false,
                        street: '',
                        neighborhood: '',
                        stateId: undefined,
                        stateAcronym: undefined,
                        cityId: undefined,
                        cityName: '',
                        cityRef: undefined,
                      })
                    }
                  }}
                  name="weak"
                  color="primary"
                />
              }
              label="Não encontrei meu logradouro"
            />
          </Grid>
        )}

        {searched && notFound && !canDisplayWeak() && (
          <Grid item xs={12}>
            <FormHelperText error>
              {outsideZone
                ? 'Este CEP fica fora da área de atendimento desta chamada. Só podem se inscrever candidatos que residem no zoneamento das escolas participantes.'
                : 'CEP não encontrado. Verifique o número informado e tente novamente.'}
            </FormHelperText>
          </Grid>
        )}
      </Grid>
    </>
  )
}

export default Search
